import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Başlangıç tarihi ve baz sipariş sayısı (15 Nisan 2026, 64 adet)
const BASE_DATE = new Date('2026-04-15T00:00:00');
const BASE_COUNT = 64;

// Her gün için deterministik bir artış üretir (2-8 arası, tohumla rastgele)
function getDailyIncrement(dayIndex: number): number {
    // Basit tohumlanmış pseudo-random (her çalışmada aynı sonucu verir)
    const seed = dayIndex * 2654435761;
    return 2 + (seed % 7);
}

function getOrderCount(): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const base = new Date(BASE_DATE);
    base.setHours(0, 0, 0, 0);
    const diffDays = Math.max(0, Math.floor((today.getTime() - base.getTime()) / (1000 * 60 * 60 * 24)));

    let count = BASE_COUNT;
    for (let i = 1; i <= diffDays; i++) {
        count += getDailyIncrement(i);
    }
    return count;
}

// Ayın ismini Türkçe döndürür
function getCurrentMonthName(): string {
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
        'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    return months[new Date().getMonth()];
}

const OrderCountPopup: React.FC = () => {
    const location = useLocation();
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [count, setCount] = useState(0);
    const [displayCount, setDisplayCount] = useState(0);
    const timerRef = useRef<any>(null);

    useEffect(() => {
        const real = getOrderCount();
        setCount(real);
        // 3.5 saniye sonra göster
        timerRef.current = setTimeout(() => {
            setVisible(true);
        }, 3500);
        return () => clearTimeout(timerRef.current);
    }, []);

    // Sayı animasyonu
    useEffect(() => {
        if (!visible) return;
        setDisplayCount(0);
        let current = 0;
        const target = count;
        const step = Math.ceil(target / 40);
        const interval = setInterval(() => {
            current = Math.min(current + step, target);
            setDisplayCount(current);
            if (current >= target) clearInterval(interval);
        }, 30);
        return () => clearInterval(interval);
    }, [visible, count]);

    if (!visible || dismissed) return null;
    if (location.pathname !== '/') return null;

    return (
        <div
            className="fixed bottom-24 left-4 z-[998] animate-slide-in"
            style={{ animation: 'slideInLeft 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
        >
            <style>{`
                @keyframes slideInLeft {
                    from { transform: translateX(-120%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes pulse-ring {
                    0% { transform: scale(1); opacity: 0.7; }
                    100% { transform: scale(1.5); opacity: 0; }
                }
                .pulse-ring::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: 50%;
                    background: #e45078;
                    animation: pulse-ring 1.5s ease-out infinite;
                }
            `}</style>

            <div className="relative bg-white rounded-2xl shadow-2xl border border-rose-100 p-4 pr-5 max-w-[240px] flex items-center gap-3"
                style={{ boxShadow: '0 8px 32px rgba(228, 80, 120, 0.18)' }}
            >
                {/* Kapatma butonu */}
                <button
                    onClick={() => setDismissed(true)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-gray-200 hover:bg-gray-300 rounded-full flex items-center justify-center text-gray-500 text-xs transition-colors"
                    aria-label="Kapat"
                >
                    ✕
                </button>

                {/* İkon */}
                <div className="relative flex-shrink-0 w-10 h-10 flex items-center justify-center">
                    <div className="pulse-ring absolute w-10 h-10 rounded-full bg-rose-100" />
                    <div className="relative z-10 w-10 h-10 rounded-full bg-gradient-to-br from-[#e45078] to-[#c02f5a] flex items-center justify-center text-white text-lg shadow-md">
                        🎊
                    </div>
                </div>

                {/* Metin */}
                <div className="min-w-0">
                    <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide leading-tight">
                        {getCurrentMonthName()} Siparişleri
                    </p>
                    <p className="text-gray-800 font-extrabold text-xl leading-tight tabular-nums">
                        {displayCount.toLocaleString('tr-TR')}
                        <span className="text-[#e45078]"> çiftimizin</span>
                    </p>
                    <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
                        bu ay anılarını sonsuza kadar yaşatıyoruz ✨
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OrderCountPopup;
